import { Module } from 'vuex'
import { BillingStoreState } from '@/modules/billing-module/store/billing-store.model'
import { RootStoreState } from '@/store/root/root-store.model'
import { BillingGridType } from '@/modules/billing-module/components/grid/config/datasources/billing-grid-datasource-factory'
import { BillingCaseCategory } from '@/api/services/billing/case-grid/billing-case.service'
import { UIComponentCustomFilterResponse } from '@/api/services/common/ui-component/custom-filter/ui-component-custom-filter.dto'
import {
    BillingCustomFilter,
    initialBillingSelectedOption,
    initialTimeFilter
} from '@/modules/billing-module/components/sidebar/billing-sidebar.model'
import { VtStore } from '@/store/vt-store'
import { findNodeParentByPropertyValue } from '@/components/sideBar/sideBarUtils'
import { billingSidebarOptions } from '@/modules/billing-module/components/sidebar/billing-sidebar-options'
import { StrictNullable } from '@/shared/types/app.types'

const initialBillingStoreState =
    (): BillingStoreState => ({
        // sidebar
        selectedOption: initialBillingSelectedOption,
        timeFilter: initialTimeFilter,
        // grid
        gridType: BillingGridType.CASES,
        category: BillingCaseCategory.HAS_AUTOMATIC_BILLABLE_CONFLICTS,
        filterModel: {},
        masterSelectedRows: [],
        detailSelectedRows: []
    })

const module: Module<BillingStoreState, RootStoreState> = {
    namespaced: true,
    state: initialBillingStoreState,
    getters: {
        // sidebar
        selectedOption:
            (state): BillingStoreState['selectedOption'] => state.selectedOption,
        selectedParentOption:
            (state): StrictNullable<any> =>
                findNodeParentByPropertyValue('id', state.selectedOption?.id, billingSidebarOptions().expanded),
        timeFilter:
            (state): BillingStoreState['timeFilter'] => state.timeFilter,
        // grid
        gridType:
            (state): BillingStoreState['gridType'] => state.gridType,
        category:
            (state): BillingStoreState['category'] => state.category,
        filterModel:
            (state): BillingStoreState['filterModel'] => state.filterModel,
        masterSelectedRows:
            (state): BillingStoreState['masterSelectedRows'] => state.masterSelectedRows,
        detailSelectedRows:
            (state): BillingStoreState['detailSelectedRows'] => state.detailSelectedRows
    },
    mutations: {
        updateSelectedOption:
            (state, payload: BillingStoreState['selectedOption']): void => {
                state.selectedOption = payload
            },
        updateGridType:
            (state, payload: BillingStoreState['gridType']): void => {
                state.gridType = payload
            },
        updateCategory:
            (state, payload: BillingStoreState['category']): void => {
                state.category = payload
            },
        updateFilterModel:
            (state, payload: BillingStoreState['filterModel']): void => {
                state.filterModel = payload
            },
        updateMasterSelectedRows:
            (state, payload: BillingStoreState['masterSelectedRows']): void => {
                state.masterSelectedRows = payload
            },
        updateDetailSelectedRows:
            (state, payload: BillingStoreState['detailSelectedRows']): void => {
                state.detailSelectedRows = payload
            },
        updateTimeFilter:
            (state, payload: BillingStoreState['timeFilter']): void => {
                state.timeFilter = payload
            }
    },
    actions: {
        resetState:
            ({ state }): void => {
                Object.assign(state, initialBillingStoreState())
            },
        resetGridState:
            ({ commit }): void => {
                const { masterSelectedRows, detailSelectedRows } = initialBillingStoreState()
                commit('updateMasterSelectedRows', masterSelectedRows)
                commit('updateDetailSelectedRows', detailSelectedRows)
            },
        setSelectedOption:
            ({ commit, getters }, payload: BillingStoreState['selectedOption']): void => {
                if (payload?.hasOwnProperty('filterName')) {
                    // selected option is a custom filter
                    const customFilter: UIComponentCustomFilterResponse<BillingCustomFilter> = payload
                    commit('updateSelectedOption', { ...customFilter, label: customFilter.filterName })
                    commit('updateGridType', customFilter.filterCriteria.gridType)
                    commit('updateCategory', customFilter.filterCriteria.category)
                    commit('updateTimeFilter', customFilter.filterCriteria.timeFilter)
                    commit('updateFilterModel', customFilter.filterCriteria.filterModel)
                } else {
                    const { filterModel } = initialBillingStoreState()
                    commit('updateSelectedOption', payload)
                    commit('updateGridType', getters['selectedParentOption']?.data?.gridType)
                    commit('updateCategory', payload?.data?.category)
                    commit('updateFilterModel', payload?.filterModel ?? filterModel)
                }
            },
        setTimeFilter:
            ({ commit, getters }, payload: BillingStoreState['timeFilter']): void => {
                commit('updateTimeFilter', payload)
                commit('updateFilterModel', getters['filterModel'])
            }
    }
}
export const billingStore = new VtStore<BillingStoreState>('billing', module)
